import { getResData, httpClient } from "utils";
import { DEFAULT_PATH_EMISSIONS, DEFAULT_PATH_CONSUMPTION, DEFAULT_PATH_FINANCIALS, DATA_READINESS_URL, TARGETS_DASHBOARD_URL } from "./consts";

export const DASHBOARD = {
  VARIANCE: "/variance/yoy",
  COMPARISON: "/variance/comparison",
  DATE_RANGE: (chartType: string) => `/range/${chartType}`,
  YOY: (chartType: string) => `/yoy/${chartType}`,
  SCOPE: (chartType: string) => `/scope/${chartType}`
};

export interface DefaultChartDTO {
  countryRegions: string[] | null;
  countries: string[] | null;
  facilityTypes: string[] | null;
  businessUnits: string[] | null;
  scopes: string[] | null;
  utilityTypeIds: number[] | null;
  siteIds: number[] | null;
  accountNumbers: string[] | null;
  meterNumbers: string[] | null;
};

export interface VarianceChartDTO extends DefaultChartDTO {
  compareFromDate: string;
  compareToDate: string;
};

export interface VarianceComparisonDTO extends VarianceChartDTO {
  comparisonPeriod: string;
};

export interface DateRangeDTO extends VarianceChartDTO {
  groupBy: string;
};

export interface YoYDTO extends DefaultChartDTO {
  yearsToCompare: number;
};

export interface SingleYearByScopeDTO extends DefaultChartDTO {
  fiscalYear: string;
};

// TODO: TEMPORAL - REMOVE when Intensity external link functionality is removed
export interface IntensityEmbedUrlResponse {
  embedUrl: string;
};

// VARIANCE UPDATES
export const updateVarianceEmissions = (varianceRequest: VarianceChartDTO) =>
  httpClient.post<VarianceChartDTO>(`${DEFAULT_PATH_EMISSIONS + DASHBOARD.VARIANCE}`, varianceRequest);

export const updateVarianceConsumption = (varianceRequest: VarianceChartDTO) =>
  httpClient.post<VarianceChartDTO>(`${DEFAULT_PATH_CONSUMPTION + DASHBOARD.VARIANCE}`, varianceRequest);

export const updateVarianceFinancials = (varianceRequest: VarianceChartDTO) =>
  httpClient.post<VarianceChartDTO>(`${DEFAULT_PATH_FINANCIALS + DASHBOARD.VARIANCE}`, varianceRequest);

export const updateVarianceComparisonEmissions = (comparisonRequest: VarianceComparisonDTO) =>
  httpClient.post<VarianceComparisonDTO>(`${DEFAULT_PATH_EMISSIONS + DASHBOARD.COMPARISON}`, comparisonRequest);

export const updateVarianceComparisonConsumption = (comparisonRequest: VarianceComparisonDTO) =>
  httpClient.post<VarianceComparisonDTO>(`${DEFAULT_PATH_CONSUMPTION + DASHBOARD.COMPARISON}`, comparisonRequest);

export const updateVarianceComparisonFinancials = (comparisonRequest: VarianceComparisonDTO) =>
  httpClient.post<VarianceComparisonDTO>(`${DEFAULT_PATH_FINANCIALS + DASHBOARD.COMPARISON}`, comparisonRequest);

// DATE RANGE UPDATES
export const updateDateRangeEmissions = ({ chartType, dateRangeRequest }: { dateRangeRequest: DateRangeDTO, chartType: string }) =>
  httpClient.post<DateRangeDTO>(`${DEFAULT_PATH_EMISSIONS + DASHBOARD.DATE_RANGE(chartType)}`, dateRangeRequest);

export const updateDateRangeConsumption = ({ chartType, dateRangeRequest }: { dateRangeRequest: DateRangeDTO, chartType: string }) =>
  httpClient.post<DateRangeDTO>(`${DEFAULT_PATH_CONSUMPTION + DASHBOARD.DATE_RANGE(chartType)}`, dateRangeRequest);

export const updateDateRangeFinancials = ({ chartType, dateRangeRequest }: { dateRangeRequest: DateRangeDTO, chartType: string }) =>
  httpClient.post<DateRangeDTO>(`${DEFAULT_PATH_FINANCIALS + DASHBOARD.DATE_RANGE(chartType)}`, dateRangeRequest);

// YOY UPDATES
export const updateYoYEmissions = ({ chartType, YoYRequest }: { chartType: string; YoYRequest: YoYDTO }) =>
  httpClient.post<YoYDTO>(`${DEFAULT_PATH_EMISSIONS + DASHBOARD.YOY(chartType)}`, YoYRequest);

export const updateYoYConsumption = ({ chartType, YoYRequest }: { chartType: string; YoYRequest: YoYDTO }) =>
  httpClient.post<YoYDTO>(`${DEFAULT_PATH_CONSUMPTION + DASHBOARD.YOY(chartType)}`, YoYRequest);

export const updateYoYFinancials = ({ chartType, YoYRequest }: { chartType: string; YoYRequest: YoYDTO }) =>
  httpClient.post<YoYDTO>(`${DEFAULT_PATH_FINANCIALS + DASHBOARD.YOY(chartType)}`, YoYRequest);

// SINGLE YEAR BY SCOPE UPDATES
export const updateSingleYearByScopeEmissions = ({ chartType, singleYearByScopeRequest }: { chartType: string; singleYearByScopeRequest: SingleYearByScopeDTO }) =>
  httpClient.post<SingleYearByScopeDTO>(`${DEFAULT_PATH_EMISSIONS + DASHBOARD.SCOPE(chartType)}`, singleYearByScopeRequest);

export const updateSingleYearByScopeConsumption = ({ chartType, singleYearByScopeRequest }: { chartType: string; singleYearByScopeRequest: SingleYearByScopeDTO }) =>
  httpClient.post<SingleYearByScopeDTO>(`${DEFAULT_PATH_CONSUMPTION + DASHBOARD.SCOPE(chartType)}`, singleYearByScopeRequest);

export const updateSingleYearByScopeFinancials = ({ chartType, singleYearByScopeRequest }: { chartType: string; singleYearByScopeRequest: SingleYearByScopeDTO }) =>
  httpClient.post<SingleYearByScopeDTO>(`${DEFAULT_PATH_FINANCIALS + DASHBOARD.SCOPE(chartType)}`, singleYearByScopeRequest);

// TODO: TEMPORAL - REMOVE when Intensity external link functionality is removed
export const getIntensityEmbedUrl = (): Promise<IntensityEmbedUrlResponse> =>
  httpClient.post<IntensityEmbedUrlResponse>("/intensity/v3").then(getResData);

export const getDataReadinessUrl = (): Promise<{ embedUrl: string }> =>
  httpClient.post<{ embedUrl: string }>(DATA_READINESS_URL).then(getResData);

export const getTargetsUrl = (): Promise<{ embedUrl: string }> =>
  httpClient.post<{ embedUrl: string }>(TARGETS_DASHBOARD_URL).then(getResData);
